import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Sparkles,
  Brain,
  LayoutDashboard,
  Monitor,
  Target,
  BarChart3,
  Cloud,
  Settings,
  Wand2,
  Zap,
  Star,
  Command as CommandIcon,
  KeyboardIcon,
  EyeOff,
  Layout,
  Compass,
} from 'lucide-react';
import { useUIMode } from '@/context/UIModeContext';
import { useGuideTour } from '@/context/GuideTourContext';
import { CommandPalette, type Command } from './CommandPalette';
import { skillService, type Skill, type SkillCategory } from '@/services/skills';
import { HUBS } from '@/routes/hubRoutes';
import { toast } from 'sonner';

const RECENT_KEY = 'khiimori.recentPaletteCommands';
const RECENT_LIMIT = 5;

const HUB_ICON_MAP: Record<string, React.ElementType> = {
  home: LayoutDashboard,
  tabs: Monitor,
  ai: Brain,
  apps: Sparkles,
  productivity: Target,
  insights: BarChart3,
  sync: Cloud,
  settings: Settings,
};

const CATEGORY_ICON: Record<SkillCategory, React.ElementType> = {
  generate: Wand2,
  analyze: Brain,
  organize: Sparkles,
  predict: Sparkles,
  optimize: Zap,
  collaborate: Sparkles,
};

const STATIC_COMMANDS: Command[] = HUBS.map((hub) => ({
  id: `hub:${hub.id}`,
  label: `Go to ${hub.title}`,
  description: `Open the ${hub.title} hub`,
  icon: (() => {
    const Icon = HUB_ICON_MAP[hub.id] ?? LayoutDashboard;
    return <Icon className="w-4 h-4" />;
  })(),
  category: 'Navigation',
  keywords: [hub.title.toLowerCase(), 'hub', 'navigate', hub.id],
  action: () => {
    // Navigation is handled by the consumer via onNavigate hook below.
  },
}));

const FALLBACK_COMMANDS: Command[] = [
  {
    id: 'open-settings',
    label: 'Open Settings',
    description: 'Configure Khiimori',
    icon: <Settings className="w-4 h-4" />,
    category: 'Navigation',
    keywords: ['settings', 'preferences', 'config'],
    action: () => undefined,
  },
  {
    id: 'show-shortcuts',
    label: 'Show keyboard shortcuts',
    description: 'Open the keyboard shortcuts page',
    icon: <KeyboardIcon className="w-4 h-4" />,
    category: 'Help',
    keywords: ['shortcuts', 'keys', 'help'],
    action: () => undefined,
  },
];

interface SkillAwareCommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * SkillAwareCommandPalette — a higher-level wrapper around the existing
 * CommandPalette presentational component. It:
 *  - Fetches the live skill catalog from `/api/v1/skills`.
 *  - Registers one Command per enabled skill (navigates to `frontend_route`).
 *  - Registers navigation commands for each of the 8 hubs.
 *  - Tracks the last 5 used commands in `localStorage` and surfaces them
 *    in the "Recent" group when the palette opens.
 *
 * The existing `CommandPalette` component is used unchanged; this wrapper
 * only builds the `Command[]` array passed in.
 */
export function SkillAwareCommandPalette({ isOpen, onClose }: SkillAwareCommandPaletteProps) {
  const navigate = useNavigate();
  const { startTour } = useGuideTour();
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [recent, setRecent] = useState<string[]>([]);
  const recentRef = useRef<string[]>([]);

  // Load recents on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          setRecent(parsed.filter((x): x is string => typeof x === 'string'));
        }
      }
    } catch {
      // ignore malformed storage
    }
  }, []);

  // Load skills on mount
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    skillService
      .listSkills({})
      .then((list) => {
        if (mounted) setSkills(list);
      })
      .catch(() => {
        if (mounted) setSkills([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const { setUIMode } = useUIMode();

  const recordRecent = useCallback((id: string) => {
    setRecent((prev) => {
      const next = [id, ...prev.filter((x) => x !== id)].slice(0, RECENT_LIMIT);
      try {
        localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      } catch {
        // ignore quota errors
      }
      return next;
    });
  }, []);

  recentRef.current = recent;

  const commands = useMemo<Command[]>(() => {
    // Static hub navigation
    const hubCommands: Command[] = HUBS.map((hub) => ({
      id: `hub:${hub.id}`,
      label: `Go to ${hub.title}`,
      description: `Open the ${hub.title} hub`,
      icon: (() => {
        const Icon = HUB_ICON_MAP[hub.id] ?? LayoutDashboard;
        return <Icon className="w-4 h-4" />;
      })(),
      category: 'Navigation',
      keywords: [hub.title.toLowerCase(), 'hub', 'navigate', hub.id],
      action: () => {
        recordRecent(`hub:${hub.id}`);
        navigate(hub.path);
      },
    }));

    const skillCommands: Command[] = skills
      .filter((s) => s.is_enabled)
      .map((s) => {
        const Icon = CATEGORY_ICON[s.category] ?? Sparkles;
        return {
          id: `skill:${s.id}`,
          label: s.name,
          description: s.description,
          icon: <Icon className="w-4 h-4" />,
          category: `Skills · ${s.category}`,
          keywords: [s.id, s.name.toLowerCase(), s.category, s.agent, ...(s.capabilities ?? [])],
          action: () => {
            recordRecent(`skill:${s.id}`);
            if (s.frontend_route && s.frontend_route.length > 0) {
              navigate(s.frontend_route);
              return;
            }
            void (async () => {
              try {
                const res = await skillService.executeSkill(s.id, {});
                if (res.status === 'success') {
                  toast.success(res.message || `Ran "${s.name}"`);
                } else if (res.status === 'skipped') {
                  toast.message(res.message || 'Skill has no executor; deep-link only.');
                } else {
                  toast.error(res.message || `Failed: ${s.name}`);
                }
              } catch (e) {
                toast.error(`Failed to run ${s.name}: ${(e as Error).message}`);
              }
            })();
          },
        } satisfies Command;
      });

    const fallback: Command[] = [
      {
        id: 'open-settings',
        label: 'Open Settings',
        description: 'Configure Khiimori',
        icon: <Settings className="w-4 h-4" />,
        category: 'Navigation',
        keywords: ['settings', 'preferences', 'config'],
        action: () => {
          recordRecent('open-settings');
          navigate('/settings');
        },
      },
      {
        id: 'start-guide-tour',
        label: 'Start Guided App Tour',
        description: 'Interactive walkthrough highlighting all key features and hubs',
        icon: <Compass className="w-4 h-4 text-primary-400" />,
        category: 'Help',
        keywords: ['tour', 'guide', 'walkthrough', 'help', 'tutorial', 'onboarding'],
        action: () => {
          recordRecent('start-guide-tour');
          onClose();
          setTimeout(() => startTour(0), 100);
        },
      },
      {
        id: 'show-shortcuts',
        label: 'Show keyboard shortcuts',
        description: 'Open the keyboard shortcuts page',
        icon: <KeyboardIcon className="w-4 h-4" />,
        category: 'Help',
        keywords: ['shortcuts', 'keys', 'help'],
        action: () => {
          recordRecent('show-shortcuts');
          navigate('/settings?section=shortcuts');
        },
      },
      {
        id: 'ui-mode-invisible',
        label: 'Switch to Invisible Mode',
        description: 'Hide all UI elements and run silently',
        icon: <EyeOff className="w-4 h-4" />,
        category: 'UI Modes',
        keywords: ['invisible', 'hide', 'ui', 'mode', 'zen'],
        action: () => {
          recordRecent('ui-mode-invisible');
          setUIMode('invisible');
          toast.success('Switched to Invisible Mode');
        },
      },
      {
        id: 'ui-mode-ambient',
        label: 'Switch to Ambient Mode',
        description: 'Show subtle presence indicators',
        icon: <Sparkles className="w-4 h-4" />,
        category: 'UI Modes',
        keywords: ['ambient', 'subtle', 'ui', 'mode'],
        action: () => {
          recordRecent('ui-mode-ambient');
          setUIMode('ambient');
          toast.success('Switched to Ambient Mode');
        },
      },
      {
        id: 'ui-mode-explicit',
        label: 'Switch to Explicit Mode',
        description: 'Show full control surfaces',
        icon: <Layout className="w-4 h-4" />,
        category: 'UI Modes',
        keywords: ['explicit', 'full', 'ui', 'mode'],
        action: () => {
          recordRecent('ui-mode-explicit');
          setUIMode('explicit');
          toast.success('Switched to Explicit Mode');
        },
      },
    ];

    return [...hubCommands, ...fallback, ...skillCommands];
    // We deliberately omit navigate/recordRecent from deps so the array
    // isn't rebuilt on every render. They are stable references via useCallback.
  }, [skills, navigate, recordRecent, setUIMode]);

  // Override action handlers with side-effect-free versions that
  // navigate via the router and record recents.
  const wrappedCommands = useMemo<Command[]>(
    () =>
      commands.map((cmd) => ({
        ...cmd,
        // The wrapped `action` from `useMemo` already calls navigate + recordRecent.
        action: cmd.action,
      })),
    [commands],
  );

  return (
    <CommandPalette
      commands={wrappedCommands}
      isOpen={isOpen}
      onClose={onClose}
      recentCommands={recent}
    />
  );
}

export default SkillAwareCommandPalette;
